/**
 * The outline: the list of every section, always visible and always one click
 * away. The page carries its links; this marks the section the reader is in and
 * sends a click to the camera.
 */
import type { Viewport } from '../viewport'

export function createOutline(
  nav: HTMLElement,
  sections: HTMLElement[],
  jump: (section: HTMLElement) => void
) {
  /* the links are in the page already, one to each section, so the outline works without scripts */
  const links = Array.from(nav.querySelectorAll('a'))
  if (links.length !== sections.length) {
    throw new Error(`the outline links ${links.length} sections, the page has ${sections.length}`)
  }
  links.forEach((a, i) => {
    a.addEventListener('click', (e) => {
      e.preventDefault()
      jump(sections[i])
    })
  })

  return {
    /** Marks the section the reader is in. */
    mark(vp: Viewport) {
      let cur = 0
      const mid = vp.height * 0.5
      sections.forEach((el, i) => {
        const r = el.getBoundingClientRect()
        const probe = el.classList.contains('stop')
          ? r.top + r.height / 2 - vp.height * 0.35
          : r.top
        if (probe <= mid) cur = i
      })
      if (window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 4)
        cur = sections.length - 1
      links.forEach((a, i) => a.classList.toggle('on', i === cur))
    },
    /** Points at a section's entry, as the place list does while a name is hovered; no argument stops pointing. */
    peek(section?: HTMLElement) {
      nav.classList.toggle('peek', !!section)
      links.forEach((a, i) => a.classList.toggle('target', sections[i] === section))
    },
    show(on: boolean) {
      nav.classList.toggle('on', on)
    },
  }
}

export type Outline = ReturnType<typeof createOutline>
